import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  Button,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  Receipt,
  AttachMoney,
  CalendarToday,
  Business,
  Visibility,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Invoice, Account, Contact, Opportunity } from '../services/crmService';

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Detail dialog state
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await crmService.getInvoices();
      const invoicesData = Array.isArray(response) ? response :
                          response.invoices ? response.invoices :
                          response.data ? response.data : [];
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setSnackbar({ open: true, message: 'Failed to fetch invoices', severity: 'error' });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Get account name from invoice
  const getAccountName = (account: string | Account | undefined): string => {
    if (!account) return '-';
    if (typeof account === 'string') return account;
    return account.name || '-';
  };

  // Filter invoices based on search
  const filteredInvoices = invoices.filter(invoice =>
    `${invoice.invoiceNumber} ${getAccountName(invoice.account)}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color and icon
  const getStatusConfig = (status: string) => {
    const config: Record<string, { color: 'success' | 'warning' | 'error' | 'info' | 'default', icon: React.ReactNode }> = {
      'Paid': { color: 'success', icon: <CheckCircle fontSize="small" /> },
      'Partially Paid': { color: 'info', icon: <AttachMoney fontSize="small" /> },
      'Sent': { color: 'info', icon: <Receipt fontSize="small" /> },
      'Viewed': { color: 'info', icon: <Visibility fontSize="small" /> },
      'Draft': { color: 'default', icon: <Receipt fontSize="small" /> },
      'Overdue': { color: 'error', icon: <ErrorIcon fontSize="small" /> },
      'Cancelled': { color: 'error', icon: <ErrorIcon fontSize="small" /> },
      'Refunded': { color: 'warning', icon: <Warning fontSize="small" /> },
    };
    return config[status] || { color: 'default', icon: <Receipt fontSize="small" /> };
  };

  // Get contact name from invoice
  const getContactName = (contact: string | Contact | undefined): string => {
    if (!contact) return '-';
    if (typeof contact === 'string') return contact;
    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '-';
  };

  // Get opportunity name from invoice
  const getOpportunityName = (opportunity: string | Opportunity | undefined): string => {
    if (!opportunity) return '-';
    if (typeof opportunity === 'string') return opportunity;
    return opportunity.name || '-';
  };

  // Format currency
  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle view invoice details
  const handleViewInvoice = (invoice: Invoice) => {
    setViewingInvoice(invoice);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setViewingInvoice(null);
  };

  // Stats calculations
  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(i => i.status === 'Paid').length;
  const overdueInvoices = invoices.filter(i => i.status === 'Overdue').length;
  const totalPaid = invoices.reduce((sum, i) => sum + (i.amountPaid || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Receipt sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalInvoices}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Invoices</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{paidInvoices}</Typography>
                  <Typography variant="body2" color="text.secondary">Paid</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorIcon sx={{ color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{overdueInvoices}</Typography>
                  <Typography variant="body2" color="text.secondary">Overdue</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoney sx={{ color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{formatCurrency(totalPaid)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Collected</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Invoices
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchInvoices}
          >
            Refresh
          </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search invoices by number or account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Invoices Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={7}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {invoice.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {getAccountName(invoice.account)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            {formatDate(invoice.issueDate)}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(invoice.totalAmount, invoice.currency)}
                          </Typography>
                          {invoice.amountPaid && invoice.amountPaid > 0 && invoice.amountPaid < invoice.totalAmount && (
                            <Typography variant="caption" color="text.secondary">
                              Paid: {formatCurrency(invoice.amountPaid, invoice.currency)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            size="small"
                            color={getStatusConfig(invoice.status).color}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewInvoice(invoice)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Invoice Detail Dialog */}
        <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Invoice Details
          </DialogTitle>
          <DialogContent>
            {viewingInvoice && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">
                      {viewingInvoice.invoiceNumber}
                    </Typography>
                    <Chip
                      label={viewingInvoice.status}
                      color={getStatusConfig(viewingInvoice.status).color}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Account</Typography>
                  <Typography>{getAccountName(viewingInvoice.account)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact</Typography>
                  <Typography>{getContactName(viewingInvoice.contact)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Opportunity</Typography>
                  <Typography>{getOpportunityName(viewingInvoice.opportunity)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payment Terms</Typography>
                  <Typography>{viewingInvoice.paymentTerms || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Issue Date</Typography>
                  <Typography>{formatDate(viewingInvoice.issueDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Due Date</Typography>
                  <Typography>{formatDate(viewingInvoice.dueDate)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Line Items */}
                {viewingInvoice.lineItems && viewingInvoice.lineItems.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Line Items</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingInvoice.lineItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.unitPrice, viewingInvoice.currency)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.total, viewingInvoice.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Totals */}
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal:</Typography>
                    <Typography>{formatCurrency(viewingInvoice.subtotal, viewingInvoice.currency)}</Typography>
                  </Box>
                  {viewingInvoice.totalDiscount && viewingInvoice.totalDiscount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Discount:</Typography>
                      <Typography color="error">-{formatCurrency(viewingInvoice.totalDiscount, viewingInvoice.currency)}</Typography>
                    </Box>
                  )}
                  {viewingInvoice.totalTax && viewingInvoice.totalTax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Tax:</Typography>
                      <Typography>{formatCurrency(viewingInvoice.totalTax, viewingInvoice.currency)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography fontWeight="bold">Total:</Typography>
                    <Typography fontWeight="bold">{formatCurrency(viewingInvoice.totalAmount, viewingInvoice.currency)}</Typography>
                  </Box>
                  {viewingInvoice.amountPaid && viewingInvoice.amountPaid > 0 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="success.main">Amount Paid:</Typography>
                        <Typography color="success.main">{formatCurrency(viewingInvoice.amountPaid, viewingInvoice.currency)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main" fontWeight="bold">Amount Due:</Typography>
                        <Typography color="error.main" fontWeight="bold">{formatCurrency(viewingInvoice.amountDue || 0, viewingInvoice.currency)}</Typography>
                      </Box>
                    </>
                  )}
                </Grid>

                {viewingInvoice.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography>{viewingInvoice.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default InvoicesPage;
